import { useCallback, useEffect, useRef, useState } from "react";
import type { MindMapDto } from "@mmm/contracts";
import { createMindMap } from "../../api/commands.js";
import type { SubjectWithChildren } from "../../types.js";

interface CreatedMap {
  scope: string;
  map: MindMapDto;
}

/** Resolves or lazily creates the one map belonging to a subject/chapter scope. */
export function useEnsuredMap(
  subject: SubjectWithChildren,
  chapterId: string | null,
  title: string
): { map: MindMapDto | null; error: boolean; retry: () => void } {
  const scope = `${subject.id}:${chapterId ?? "subject"}`;
  const existingMap = subject.mindMaps.find((mindMap) => (mindMap.chapterId ?? null) === chapterId) ?? null;
  const [createdMap, setCreatedMap] = useState<CreatedMap | null>(null);
  const [failedScope, setFailedScope] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const inFlight = useRef(new Map<string, Promise<MindMapDto>>());
  const map = existingMap ?? (createdMap?.scope === scope ? createdMap.map : null);

  useEffect(() => {
    if (map) {
      return;
    }

    let active = true;
    let promise = inFlight.current.get(scope);
    if (!promise) {
      promise = createMindMap({ subjectId: subject.id, chapterId, title });
      inFlight.current.set(scope, promise);
    }
    promise.then((created) => {
      if (!active) return;
      setCreatedMap({ scope, map: created });
      setFailedScope(null);
    }).catch(() => {
      if (active) setFailedScope(scope);
    }).finally(() => {
      if (inFlight.current.get(scope) === promise) inFlight.current.delete(scope);
    });

    return () => { active = false; };
  }, [map, scope, subject.id, chapterId, title, retryToken]);

  const retry = useCallback(() => {
    setFailedScope(null);
    setRetryToken((value) => value + 1);
  }, []);

  return { map, error: !map && failedScope === scope, retry };
}
